import React, { useState, useEffect, useRef } from 'react';
import './VideoHub.css';

function VideoHub() {
  const [heroVideo, setHeroVideo] = useState(null);
  const [regularVideos, setRegularVideos] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [playlists, setPlaylists] = useState({ featured: [], playlists: [] });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filter, setFilter] = useState('all');
  const [hoverPreview, setHoverPreview] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(null);
  
  const playerRefs = useRef({});

  useEffect(() => {
    fetchAllData();
    loadYouTubeAPI();
  }, []);

  const loadYouTubeAPI = () => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }
  };

  const fetchAllData = async () => {
    try {
      const [heroRes, videosRes, shortsRes, playlistsRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos/hero`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos/regular?limit=12`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/videos/shorts?limit=20`),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/playlists`)
      ]);

      if (heroRes.ok) setHeroVideo(await heroRes.json());
      if (videosRes.ok) {
        const data = await videosRes.json();
        setRegularVideos([...data.pinned, ...data.videos]);
      }
      if (shortsRes.ok) {
        const data = await shortsRes.json();
        setShorts(data.shorts);
      }
      if (playlistsRes.ok) setPlaylists(await playlistsRes.json());
    } catch (error) {
      console.error('Error fetching video hub data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoHover = (videoId, youtubeId) => {
    if (!window.YT || !window.YT.Player) return;
    
    setHoverPreview(videoId);
    
    setTimeout(() => {
      if (hoverPreview === videoId && !playerRefs.current[videoId]) {
        try {
          playerRefs.current[videoId] = new window.YT.Player(`player-${videoId}`, {
            height: '100%',
            width: '100%',
            videoId: youtubeId,
            playerVars: {
              autoplay: 1,
              mute: 1,
              controls: 0,
              modestbranding: 1,
              rel: 0,
              start: 0
            }
          });
        } catch (e) {
          console.error('Error loading preview:', e);
        }
      }
    }, 300);
  };

  const handleVideoLeave = (videoId) => {
    setHoverPreview(null);
    if (playerRefs.current[videoId]) {
      try {
        playerRefs.current[videoId].destroy();
        delete playerRefs.current[videoId];
      } catch (e) {
        console.error('Error destroying player:', e);
      }
    }
  };

  const copyLink = (youtubeId) => {
    navigator.clipboard.writeText(`https://youtube.com/watch?v=${youtubeId}`);
    alert('✅ Link copied!');
  };

  const formatViews = (views) => {
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views;
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="video-hub-loading">
        <div className="loading-spinner"></div>
        <p>Loading Video Hub...</p>
      </div>
    );
  }

  return (
    <main className="video-hub" data-testid="video-hub">
      <div className="video-hub-container">
        
        {/* HERO SECTION */}
        {heroVideo && (
          <section className="hero-section" data-testid="hero-section">
            <div className="hero-content">
              <div className="hero-video">
                <iframe
                  src={`https://www.youtube.com/embed/${heroVideo.youtube_id}?rel=0&modestbranding=1`}
                  title={heroVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
              <div className="hero-info">
                <h1 className="hero-title">{heroVideo.title}</h1>
                <div className="hero-meta">
                  <span className="hero-views">👁️ {formatViews(heroVideo.view_count || 0)} views</span>
                  <span className="hero-date">📅 {new Date(heroVideo.published_at).toLocaleDateString()}</span>
                </div>
                <p className="hero-description">{heroVideo.description?.substring(0, 200)}...</p>
                <div className="hero-actions">
                  <a 
                    href={`https://youtube.com/watch?v=${heroVideo.youtube_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Watch on YouTube
                  </a>
                  <button 
                    onClick={() => copyLink(heroVideo.youtube_id)}
                    className="btn btn-secondary"
                  >
                    📋 Copy Link
                  </button>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* CONTROLS */}
        <section className="controls-section">
          <div className="search-bar">
            <input
              type="text"
              placeholder="🔍 Search videos..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-controls">
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="most_viewed">Most Viewed</option>
            </select>
            <div className="filter-chips">
              <button 
                className={`chip ${filter === 'all' ? 'active' : ''}`}
                onClick={() => setFilter('all')}
              >
                All
              </button>
              <button 
                className={`chip ${filter === 'uploads' ? 'active' : ''}`}
                onClick={() => setFilter('uploads')}
              >
                Uploads
              </button>
              <button 
                className={`chip ${filter === 'shorts' ? 'active' : ''}`}
                onClick={() => setFilter('shorts')}
              >
                Shorts
              </button>
            </div>
          </div>
        </section>

        {/* SHORTS SECTION */}
        {shorts.length > 0 && (
          <section className="shorts-section" data-testid="shorts-section">
            <h2 className="section-title">
              <span className="title-icon">⚡</span>
              Shorts in Clutch Mode
            </h2>
            <div className="shorts-rail">
              {shorts.map((short) => (
                <div 
                  key={short.video_id} 
                  className="short-card"
                  onClick={() => setShowVideoModal(short)}
                  data-testid={`short-${short.video_id}`}
                >
                  <div className="short-thumbnail">
                    <img src={short.thumbnail} alt={short.title} />
                    <div className="short-overlay">
                      <span className="play-icon">▶</span>
                      <span className="short-duration">{formatDuration(short.duration)}</span>
                    </div>
                  </div>
                  <h3 className="short-title">{short.title}</h3>
                  <div className="short-stats">
                    <span>{formatViews(short.view_count)} views</span>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* LATEST UPLOADS */}
        <section className="latest-section" data-testid="latest-section">
          <h2 className="section-title">
            <span className="title-icon">🎮</span>
            Latest Drops
          </h2>
          <div className="videos-grid">
            {regularVideos.map((video) => (
              <div 
                key={video.video_id}
                className="video-card"
                onMouseEnter={() => handleVideoHover(video.video_id, video.youtube_id)}
                onMouseLeave={() => handleVideoLeave(video.video_id)}
                onClick={() => setShowVideoModal(video)}
                data-testid={`video-${video.video_id}`}
              >
                <div className="video-thumbnail">
                  {hoverPreview === video.video_id ? (
                    <div id={`player-${video.video_id}`} className="video-preview"></div>
                  ) : (
                    <img src={video.thumbnail} alt={video.title} />
                  )}
                  {video.is_pinned && <span className="pinned-badge">📌 Pinned</span>}
                  <span className="video-duration">{formatDuration(video.duration)}</span>
                </div>
                <div className="video-info">
                  <h3 className="video-title">{video.title}</h3>
                  <div className="video-stats">
                    <span>👁️ {formatViews(video.view_count)}</span>
                    <span>📅 {new Date(video.published_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* PLAYLISTS */}
        {playlists.playlists.length > 0 && (
          <section className="playlists-section" data-testid="playlists-section">
            <h2 className="section-title">
              <span className="title-icon">📁</span>
              Playlist Vault
            </h2>
            <div className="playlists-grid">
              {playlists.playlists.map((playlist) => (
                <div 
                  key={playlist.playlist_id} 
                  className="playlist-card"
                  data-testid={`playlist-${playlist.playlist_id}`}
                >
                  <div className="playlist-thumbnail">
                    <img src={playlist.thumbnail} alt={playlist.title} />
                    <div className="playlist-overlay">
                      <span className="playlist-count">{playlist.video_count} videos</span>
                    </div>
                  </div>
                  <div className="playlist-info">
                    <h3 className="playlist-title">{playlist.title}</h3>
                    <a 
                      href={`https://youtube.com/playlist?list=${playlist.youtube_playlist_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-sm btn-secondary"
                    >
                      Open Playlist
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="cta-section">
          <div className="cta-card">
            <h2>Watch it. Learn it. Clutch it.</h2>
            <p>Subscribe for more gaming content and walkthroughs!</p>
            <a 
              href="https://youtube.com/@clutchbabe9?si=IVZiXzIxZMTc2y5t"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-lg"
            >
              Subscribe on YouTube
            </a>
          </div>
        </section>
      </div>

      {/* VIDEO MODAL */}
      {showVideoModal && (
        <div className="video-modal" onClick={() => setShowVideoModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowVideoModal(null)}>✕</button>
            <div className="modal-player">
              <iframe
                src={`https://www.youtube.com/embed/${showVideoModal.youtube_id}?autoplay=1&rel=0`}
                title={showVideoModal.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="modal-info">
              <h2>{showVideoModal.title}</h2>
              <div className="modal-meta">
                <span>👁️ {formatViews(showVideoModal.view_count)} views</span>
                <span>📅 {new Date(showVideoModal.published_at).toLocaleDateString()}</span>
              </div>
              <p>{showVideoModal.description}</p>
              <div className="modal-actions">
                <a 
                  href={`https://youtube.com/watch?v=${showVideoModal.youtube_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  Watch on YouTube
                </a>
                <button 
                  onClick={() => copyLink(showVideoModal.youtube_id)}
                  className="btn btn-secondary"
                >
                  📋 Copy Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

export default VideoHub;
